import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    CheckCircle, 
    Clock, 
    XCircle,
    Package,
    ArrowRight,
    Loader2,
    Mail,
    Truck,
    ShoppingBag,
    Sparkles,
    CreditCard,
    Zap,
    Info,
    Wallet
} from 'lucide-react';

const OrderSuccessPage = () => {
    const { token } = useAuth();
    const { clearCart } = useCart();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');
    const paymentMethod = searchParams.get('method'); // credit, bank_transfer, stripe
    
    const [status, setStatus] = useState('loading');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [signalingSent, setSignalingSent] = useState(false);
    const [signalingLoading, setSignalingLoading] = useState(false);
    const maxPolls = 10;
    const pollCountRef = useRef(0);
    const hasInitialized = useRef(false);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // Signal instant transfer
    const handleSignalInstantTransfer = async () => {
        if (!orderId || signalingLoading) return;
        
        setSignalingLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/orders/${orderId}/signal-transfer`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                setSignalingSent(true);
                toast.success('Virement immédiat signalé ! Nous allons vérifier votre paiement rapidement.');
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Erreur lors du signalement');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setSignalingLoading(false);
        }
    };

    useEffect(() => {
        // Prevent multiple initializations
        if (hasInitialized.current) return;
        hasInitialized.current = true;
        
        // If paid by credit or bank transfer, show success immediately
        if (paymentMethod === 'credit' || paymentMethod === 'bank_transfer') {
            setStatus(paymentMethod === 'credit' ? 'success' : 'pending_payment');
            clearCart();
            return;
        }

        if (!sessionId) {
            // If no session_id but we have order_id, assume success (e.g., credit payment)
            if (orderId) {
                setStatus('success');
                clearCart();
            } else {
                setStatus('error');
            }
            return;
        }

        const checkPaymentStatus = async () => {
            try {
                const response = await axios.get(
                    `${API_URL}/api/payments/status/${sessionId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setPaymentInfo(response.data);

                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    clearCart();
                } else if (response.data.status === 'expired') {
                    setStatus('error');
                } else {
                    if (pollCountRef.current < maxPolls) {
                        pollCountRef.current += 1;
                        setTimeout(checkPaymentStatus, 2000);
                    } else {
                        setStatus('pending');
                    }
                }
            } catch (error) {
                console.error('Error checking payment status:', error);
                if (pollCountRef.current < maxPolls) {
                    pollCountRef.current += 1;
                    setTimeout(checkPaymentStatus, 2000);
                } else {
                    setStatus('error');
                }
            }
        };

        checkPaymentStatus();
    }, [sessionId, token, paymentMethod, orderId]); // Removed clearCart and pollCount

    return (
        <Layout>
            <div className="min-h-[80vh] relative overflow-hidden">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-white to-emerald-50" />
                
                {/* Decorative elements */}
                <div className="absolute top-20 left-10 w-32 h-32 bg-blue-100 rounded-full opacity-50 blur-3xl" />
                <div className="absolute top-40 right-20 w-40 h-40 bg-emerald-100 rounded-full opacity-50 blur-3xl" />
                <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-purple-100 rounded-full opacity-40 blur-2xl" />

                <div className="relative container mx-auto px-6 lg:px-8 py-16">
                    <div className="max-w-xl mx-auto">
                        
                        {/* Loading State */}
                        {status === 'loading' && (
                            <div className="text-center">
                                <div className="relative inline-block mb-8">
                                    <div className="w-28 h-28 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-200">
                                        <Loader2 className="w-14 h-14 text-white animate-spin" />
                                    </div>
                                    <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center">
                                        <Clock className="w-5 h-5 text-blue-500" />
                                    </div>
                                </div>
                                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                                    Vérification en cours...
                                </h1>
                                <p className="text-lg text-slate-600">
                                    Nous vérifions votre paiement, un instant s'il vous plaît.
                                </p>
                            </div>
                        )}

                        {/* Success State - Paid */}
                        {status === 'success' && (
                            <div className="text-center">
                                {/* Animated success icon */}
                                <div className="relative inline-block mb-8">
                                    <div className="w-32 h-32 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-xl shadow-emerald-200 animate-bounce-slow">
                                        <CheckCircle className="w-16 h-16 text-white" strokeWidth={2.5} />
                                    </div>
                                    {/* Sparkles around */}
                                    <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-amber-400 animate-pulse" />
                                    <Sparkles className="absolute -bottom-1 -left-3 w-6 h-6 text-amber-400 animate-pulse delay-150" />
                                </div>

                                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                                    Merci pour votre commande !
                                </h1>
                                
                                {orderId && (
                                    <p className="text-xl text-blue-600 font-semibold mb-6">
                                        Commande #{orderId.slice(0, 8).toUpperCase()}
                                    </p>
                                )}

                                {/* Credit payment info */}
                                {paymentMethod === 'credit' && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                                        <div className="flex items-center justify-center gap-2 text-emerald-700">
                                            <Wallet className="w-5 h-5" />
                                            <span className="font-semibold">Payé avec votre crédit compte client</span>
                                        </div>
                                    </div>
                                )}

                                {/* Info card */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-100 mb-8">
                                    <div className="flex items-start gap-4 text-left mb-4">
                                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900">Confirmation par email</p>
                                            <p className="text-slate-600 text-sm">
                                                Vous allez recevoir une confirmation de votre commande par e-mail avec tous les détails.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4 text-left">
                                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Truck className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900">Suivi de livraison</p>
                                            <p className="text-slate-600 text-sm">
                                                Dès l'expédition, vous recevrez un numéro de suivi pour suivre votre colis en temps réel.
                                            </p>
                                        </div>
                                    </div>

                                    {paymentInfo?.amount_total > 0 && (
                                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                                            <span className="text-slate-600">Montant total</span>
                                            <span className="text-2xl font-bold text-slate-900">
                                                {paymentInfo.amount_total.toFixed(2)} €
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button 
                                        onClick={() => navigate('/catalog')}
                                        size="lg"
                                        className="bg-blue-600 hover:bg-blue-700 rounded-xl text-lg px-8 py-6 shadow-lg shadow-blue-200 hover:shadow-xl transition-all"
                                        data-testid="continue-shopping"
                                    >
                                        <ShoppingBag className="w-5 h-5 mr-2" />
                                        Continuer mes achats
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="lg"
                                        onClick={() => navigate('/orders')}
                                        className="rounded-xl text-lg px-8 py-6 border-2"
                                        data-testid="view-orders"
                                    >
                                        <Package className="w-5 h-5 mr-2" />
                                        Voir mes commandes
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Pending Payment (Bank Transfer) */}
                        {status === 'pending_payment' && (
                            <div className="text-center">
                                <div className="relative inline-block mb-8">
                                    <div className="w-32 h-32 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-xl shadow-amber-200">
                                        <Clock className="w-16 h-16 text-white" />
                                    </div>
                                </div>

                                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                                    Commande enregistrée !
                                </h1>
                                
                                {orderId && (
                                    <p className="text-xl text-amber-600 font-semibold mb-6">
                                        Commande #{orderId.slice(0, 8).toUpperCase()}
                                    </p>
                                )}

                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-amber-100 mb-8">
                                    <div className="flex items-start gap-4 text-left">
                                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-6 h-6 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900">En attente de paiement</p>
                                            <p className="text-slate-600 text-sm">
                                                Vous allez recevoir un email avec les instructions pour effectuer votre virement bancaire. 
                                                Votre commande sera traitée dès réception du paiement.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Bank details */}
                                <div className="bg-slate-50 rounded-xl p-6 text-left mb-6">
                                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-blue-600" />
                                        Coordonnées bancaires
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="text-slate-500 w-24 inline-block">Bénéficiaire:</span> <span className="font-medium">Eco Mobile Solution</span></p>
                                        <p><span className="text-slate-500 w-24 inline-block">Banque:</span> <span className="font-medium">Qonto</span></p>
                                        <p><span className="text-slate-500 w-24 inline-block">IBAN:</span> <span className="font-mono font-medium">FR76 1695 8000 0183 1206 0034 363</span></p>
                                        <p><span className="text-slate-500 w-24 inline-block">BIC:</span> <span className="font-mono font-medium">QNTOFRP1XXX</span></p>
                                        <p><span className="text-slate-500 w-24 inline-block">Référence:</span> <span className="font-mono font-medium text-amber-600">CMD-{orderId?.slice(0, 8).toUpperCase()}</span></p>
                                    </div>
                                </div>

                                {/* Instant transfer button and notice */}
                                <div className="space-y-4 mb-6">
                                    {/* Virement instantané */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
                                        <div className="flex items-start gap-3">
                                            <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-blue-800 text-sm font-semibold mb-2">Virement instantané ?</p>
                                                <p className="text-blue-700 text-sm mb-3">
                                                    Signalez-le pour accélérer le traitement de votre commande.
                                                </p>
                                                {signalingSent ? (
                                                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Virement signalé ! Nous vérifions votre paiement.
                                                    </div>
                                                ) : (
                                                    <Button
                                                        onClick={handleSignalInstantTransfer}
                                                        disabled={signalingLoading}
                                                        size="sm"
                                                        className="bg-blue-600 hover:bg-blue-700"
                                                    >
                                                        {signalingLoading ? (
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        ) : (
                                                            <Zap className="w-4 h-4 mr-2" />
                                                        )}
                                                        J'ai effectué un virement immédiat
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Virement classique */}
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left">
                                        <div className="flex items-start gap-3">
                                            <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-slate-700 text-sm font-semibold mb-1">Virement classique ?</p>
                                                <p className="text-slate-600 text-sm">
                                                    Aucune action nécessaire. Votre commande sera traitée automatiquement 
                                                    dès réception du paiement (1-2 jours ouvrés).
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button 
                                        onClick={() => navigate('/orders')}
                                        size="lg"
                                        className="bg-amber-500 hover:bg-amber-600 rounded-xl text-lg px-8 py-6"
                                        data-testid="view-orders"
                                    >
                                        <Package className="w-5 h-5 mr-2" />
                                        Voir ma commande
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="lg"
                                        onClick={() => navigate('/catalog')}
                                        className="rounded-xl text-lg px-8 py-6 border-2"
                                    >
                                        Continuer mes achats
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Pending State (Stripe processing) */}
                        {status === 'pending' && (
                            <div className="text-center">
                                <div className="relative inline-block mb-8">
                                    <div className="w-32 h-32 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-xl shadow-amber-200">
                                        <Clock className="w-16 h-16 text-white animate-pulse" />
                                    </div>
                                </div>

                                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                                    Paiement en cours de traitement
                                </h1>
                                <p className="text-lg text-slate-600 mb-8">
                                    Votre paiement est en cours de vérification. Vous recevrez un email de confirmation une fois le paiement validé.
                                </p>

                                <Button 
                                    onClick={() => navigate('/orders')}
                                    size="lg"
                                    className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                                >
                                    <Package className="w-5 h-5 mr-2" />
                                    Voir mes commandes
                                </Button>
                            </div>
                        )}

                        {/* Error State */}
                        {status === 'error' && (
                            <div className="text-center">
                                <div className="relative inline-block mb-8">
                                    <div className="w-32 h-32 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center shadow-xl shadow-rose-200">
                                        <XCircle className="w-16 h-16 text-white" />
                                    </div>
                                </div>

                                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                                    Oups ! Une erreur est survenue
                                </h1>
                                <p className="text-lg text-slate-600 mb-8">
                                    Nous n'avons pas pu traiter votre paiement. Veuillez réessayer ou contacter notre support.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button 
                                        onClick={() => navigate('/cart')}
                                        size="lg"
                                        className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                                    >
                                        Retour au panier
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="lg"
                                        onClick={() => navigate('/orders')}
                                        className="rounded-xl border-2"
                                    >
                                        Voir mes commandes
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Custom animation styles */}
            <style>{`
                @keyframes bounce-slow {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }
            `}</style>
        <Footer />
        </Layout>
    );
};

export default OrderSuccessPage;
